import { resetPasswordApi } from "@/api/services/main/auth";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { useValidateToken } from "@/features/auth/hooks/useValidateToken";
import { resetPasswordSchema } from "@/features/auth/schema/resetPasswordSchema";
import { TResetPasswordForm } from "@/features/auth/types/resetPasswordForm";
import { TResetPasswordQuery } from "@/features/auth/types/resetPasswordQuery";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { getQueryFromURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

// Cooldown period: 3 seconds between submissions
const SUBMISSION_COOLDOWN_MS = 3000;

const initForm: TResetPasswordForm = {
  ownerId: "",
  newPassword: "",
  confirmNewPassword: "",
};

export const ResetPasswordForm = () => {
  const query = getQueryFromURL<TResetPasswordQuery>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSubmittingRef = useRef(false);
  const lastSubmitTimeRef = useRef<number>(0);
  const [isCooldown, setIsCooldown] = useState(false);
  
  const { isValidatingToken, isTokenValid } = useValidateToken({
    resetToken: query?.resetToken,
    email: query?.email,
    ownerId: query?.ownerId,
  });

  const methods = useForm<TResetPasswordForm>({
    mode: "onChange",
    defaultValues: { ...initForm, ownerId: query?.ownerId },
    resolver: yupResolver(resetPasswordSchema) as Resolver<TResetPasswordForm>,
  });
  const { control, formState } = methods;

  if (isValidatingToken) {
    return <LoadingScreen />;
  }

  if (!isTokenValid) {
    return null;
  }

  const onSubmit = async (data: TResetPasswordForm) => {
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTimeRef.current;

    // Prevent double submission
    if (isSubmittingRef.current) {
      return;
    }

    // Prevent rapid clicks (cooldown period)
    if (timeSinceLastSubmit < SUBMISSION_COOLDOWN_MS) {
      const remainingTime = Math.ceil((SUBMISSION_COOLDOWN_MS - timeSinceLastSubmit) / 1000);
      showError(new Error(`送信後、${remainingTime}秒待ってから再度お試しください。`));
      return;
    }

    const { newPassword } = data;
    if (!query) return;
    const { email, resetToken, ownerId } = query;
    
    isSubmittingRef.current = true;
    lastSubmitTimeRef.current = now;
    setIsCooldown(true);

    try {
      if (email && resetToken) {
        await resetPasswordApi({ email, newPassword, resetToken, ownerId });
        navigate(AppRoutes.resetPasswordComplete);
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
      isSubmittingRef.current = false; // Reset on error to allow retry
      setIsCooldown(false);
      
      // Reset cooldown after error, but still enforce minimum delay
      setTimeout(() => {
        lastSubmitTimeRef.current = 0;
      }, SUBMISSION_COOLDOWN_MS);
    }
  };

  const isDisabled = formState.isSubmitting || isSubmittingRef.current || isCooldown;

  const isRegisterRoute = pathname === AppRoutes.register;

  return (
    <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={resetPasswordSchema}>
      <Stack gap={3} width={400}>
        <Typography variant="body14" mb={2} color={text.secondary}>
          新しいパスワードを入力して、再発行してください。
        </Typography>
        <FormField control={control} name="ownerId">
          <Input type="text" size="lg" disabled readonly />
        </FormField>
        <FormField control={control} name="newPassword">
          <Input type="password" size="lg" disabled={isDisabled} />
        </FormField>
        <FormField control={control} name="confirmNewPassword">
          <Input type="password" size="lg" disabled={isDisabled} />
        </FormField>
        <Typography variant="body14" color={text.secondary}>
          ※パスワードはアルファベット大文字・小文字・数字・記号を含む12文字以上で入力してください。
        </Typography>
        <Button 
          type="submit" 
          variant="primary" 
          fullWidth 
          disabled={isDisabled}
        >
          {isRegisterRoute ? "登録" : "パスワードを再発行"}
        </Button>
      </Stack>
    </Form>
  );
};
